import { Component } from 'react'

import defaultAvatarImage from './assets/default_avatar.svg'
import Chat from './chat'
import {
  LocalizationProps,
  LocalizationProvider
} from './contexts/Localization/LocalizationContext'
import { PropsProvider, PropsProviderContext } from './contexts/PropsContext'
import {
  EmphasizeMessage,
  EventData,
  EventType,
  Icons,
  Language,
  Message,
  Sender,
  Voice
} from './globals'
import './style.scss'
import { KEY_CONVERSATION_ID } from './config'

export { sendMessage, triggerDialog } from './chat'
export type { EventType, Sender, MessageType } from './globals'

export type ChatUIEvents = {
  onMessage: (messages: Message[]) => void
  onEvent: (type: EventType, data: EventData, messages: Message[]) => void
  onSwitchLocale: (locale: Language['locale']) => void
}

export type ChatUIProps = ChatUIEvents & {
  conversationsUrl: string
  conversationStartUrl: string
  conversationContinueUrl: string
  websocketUrl: string
  configUrl: string
  transcriptionUrl: string
  baseHost: string
  senderTypes: Sender[]

  languages?: Language[]
  icons?: Icons
  customMessages?: Message[]

  // Numbers
  aivaTypingSpeed?: number
  pollingInterval?: number

  // Payload mods
  defaultHeaders?: Record<string, string>
  additionalReqBodyFields?: Record<string, string>

  // Optional strings
  title?:
    | string
    | {
        src: string
        alt: string
      }
  userAvatar?: string
  agentAvatar?: string
  inboxChatUrl?: string
  inboxId?: string
  conversationId?: string
  publicKeyName?: string
  publicKeySecret?: string
  defaultLocale?: string
  locale?: string
  channel?: string
  botName?: string
  voices?: Voice[]
  initialGreetingMessage?: string
  emphasizeDelay?: number
  emphasizeDisappear?: number
  emphasizeMessages?: EmphasizeMessage[]
  hideKnowledgeContent?: boolean
  termsMessage?: string
  

  // Booleans
  isInbox?: boolean
  withAvatars?: boolean
  isChatWindowVisible?: boolean
  readOnly?: boolean
  hideHeader?: boolean
  disableGreeting?: boolean
  isRenewSessionVisible?: boolean
  hasOtherInstances?: boolean
  isRecorderEnabled?: boolean
  freeText?: boolean
  phoneTranscript?: boolean
  autoStartRecorder?: boolean
  transcriptionDisabled?:boolean
  
  recorderMode?: 'disabled' | 'manual' | 'auto'
  // Extra events
  onMinimizeBtnClick?: () => void

  isExpanded: boolean
  onToggleExpand: () => void
}

export default class ChatUI extends Component<
  ChatUIProps & LocalizationProps & PropsProviderContext
> {
  state = {
    sessionId:
      this.props.conversationId || localStorage.getItem(KEY_CONVERSATION_ID),
    hasOtherInstances: false
  }

  componentDidUpdate = () => {
    const { isChatWindowVisible, isInbox } = this.props
    const localStorageSession = localStorage.getItem(KEY_CONVERSATION_ID)
    const mismatchSession = this.state.sessionId != localStorageSession
    if (!isChatWindowVisible && mismatchSession && !isInbox)
      this.setSessionId(null)
  }

  setSessionId = (sessionId: string | null, cb?: () => void) => {
    this.setState({ sessionId }, cb)
    if (sessionId) localStorage.setItem(KEY_CONVERSATION_ID, sessionId)
    else localStorage.removeItem(KEY_CONVERSATION_ID)
  }

  storageEventHandler = () => {
    const localSessionId = localStorage.getItem(KEY_CONVERSATION_ID)
    if (localSessionId !== this.state.sessionId)
      this.setState({
        sessionId: localSessionId,
        hasOtherInstances: true
      })
  }

  componentDidMount() {
    window.addEventListener('storage', this.storageEventHandler)
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.storageEventHandler)
  }

  render() {
    const { localization, ...restProps } = this.props
    const { sessionId, hasOtherInstances } = this.state

    return (
      <LocalizationProvider
        localization={localization}
        shouldWrap={!process.env.scriptTag}
      >
        <PropsProvider
          isChatWindowVisible={restProps.isChatWindowVisible}
          hideKnowledgeContent={restProps.hideKnowledgeContent}
        >
          <Chat
            sessionId={sessionId}
            setSessionId={this.setSessionId}
            hasOtherInstances={hasOtherInstances}
            {...restProps}
          />
        </PropsProvider>
      </LocalizationProvider>
    )
  }
}
